import { NodeViewProps, NodeViewWrapper } from "@tiptap/react";

import { TagMentionAttrs } from "./types";
import {
  TAG_MENTION_USER_MAX_WIDTH_CLASS,
  tagMentionEntityWrapperClassName,
} from "./tagMentionStyles";
import { isTagLogEntityType, TagLogEntityLink } from "./TagLogEntityLink";

import { useContentAreaSize, ContentAreaSize } from "@/components/ContentArea/size-context";
import EntityLink from "@/components/Changelog/ChangelogItem/EventHeader/EntityLink";
import { RegistryItem } from "@/components/UnifiedRegistry/types";
import { getItemLink } from "@/components/UnifiedRegistry/utils";
import { EntityIcon } from "@/components/ui/icons";
import { useUser } from "@/context/user-context";
import { EntityType } from "@/queries/folder-entities/types";

const getTagSizeClasses = (size: ContentAreaSize) => {
  switch (size) {
    case "xs":
      return {
        user: "px-1 py-0 text-xs",
        entityType: "px-1 py-0 text-xs gap-0.5",
        entity: "",
        icon: "xs" as const,
      };
    case "sm":
      return {
        user: "px-1 py-0.5 text-xs",
        entityType: "px-1.5 py-0.5 text-xs gap-0.5",
        entity: "",
        icon: "xs" as const,
      };
    case "md":
    default:
      return {
        user: "px-1 py-0.5 text-sm",
        entityType: "px-2 py-0.5 text-xs gap-1",
        entity: "",
        icon: "xs" as const,
      };
  }
};

const mapEntityTypeToEnum = (entityType: string | undefined): EntityType => {
  switch (entityType) {
    case "PROMPT":
      return EntityType.PROMPT;
    case "WORKFLOW":
      return EntityType.WORKFLOW;
    case "DATASET":
      return EntityType.DATASET;
    case "REPORT":
      return EntityType.REPORT;
    case "AB_TEST":
      return EntityType.AB_TEST;
    case "FOLDER":
      return EntityType.FOLDER;
    case "SNIPPET":
      return EntityType.SNIPPET;
    case "INPUT_VARIABLE_SET":
      return EntityType.INPUT_VARIABLE_SET;
    case "SKILL_COLLECTION":
      return EntityType.SKILL_COLLECTION;
    case "SMART_TABLE":
      return EntityType.SMART_TABLE;
    default:
      return EntityType.PROMPT;
  }
};

const getTagEntityLink = ({
  entityType,
  id,
  name,
  link,
  workspaceId,
}: {
  entityType: EntityType;
  id?: number | string;
  name: string;
  link?: string;
  workspaceId?: number | null;
}) => {
  if (link) {
    return link;
  }

  if (!workspaceId || id === undefined) {
    return undefined;
  }

  if (entityType === EntityType.SKILL_COLLECTION) {
    return `/workspace/${workspaceId}/skill-collection/${id}`;
  }

  if (entityType === EntityType.SMART_TABLE) {
    return `/workspace/${workspaceId}/smart-tables/${id}`;
  }

  return getItemLink(
    {
      id: Number(id),
      type: entityType,
      name,
    } as RegistryItem,
    workspaceId,
  );
};

const TagMentionDisplay = (props: NodeViewProps) => {
  const { node } = props;
  const attrs = node.attrs as TagMentionAttrs;
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId;
  const size = useContentAreaSize();
  const sizeClasses = getTagSizeClasses(size);

  if (attrs.tagType === "user") {
    return (
      <NodeViewWrapper
        as="span"
        title={attrs.name}
        className={`tag-mention tag-mention-user inline truncate rounded bg-blue-50 font-medium text-blue-600 ${TAG_MENTION_USER_MAX_WIDTH_CLASS} ${sizeClasses.user}`}
      >
        @{attrs.name}
      </NodeViewWrapper>
    );
  }

  if (attrs.tagType === "entityType") {
    const entityType = mapEntityTypeToEnum(attrs.entityType);
    const displayName = attrs.name.charAt(0).toUpperCase() + attrs.name.slice(1);
    return (
      <NodeViewWrapper
        as="span"
        className={`tag-mention tag-mention-entity-type inline-flex items-center rounded border border-gray-300 bg-white align-middle font-medium ${sizeClasses.entityType}`}
      >
        <EntityIcon type={entityType} size={sizeClasses.icon} className="flex-shrink-0" />
        <span>{displayName}</span>
      </NodeViewWrapper>
    );
  }

  // Entity tag - use EntityLink component
  if (isTagLogEntityType(attrs.entityType)) {
    const { link } = attrs;

    return (
      <NodeViewWrapper as="span" className={tagMentionEntityWrapperClassName(sizeClasses.entity)}>
        <TagLogEntityLink
          entityType={attrs.entityType}
          name={attrs.name}
          link={link}
          size={size}
          className="min-w-0 max-w-full"
        />
      </NodeViewWrapper>
    );
  }

  const entityType = mapEntityTypeToEnum(attrs.entityType);

  const link = getTagEntityLink({
    entityType,
    id: attrs.id,
    name: attrs.name,
    link: attrs.link,
    workspaceId,
  });

  return (
    <NodeViewWrapper as="span" className={tagMentionEntityWrapperClassName(sizeClasses.entity)}>
      <EntityLink
        type={entityType}
        name={attrs.name}
        link={link}
        size={size}
        className="min-w-0 max-w-full"
      />
    </NodeViewWrapper>
  );
};

export default TagMentionDisplay;
